// 领域类型 —— 严格对齐 core-api 的真实返回结构（{data} 信封；错误为 {error}）。
// 列名来自 apps/core-api/migrations/001_initial.sql，不使用任何模拟字段。

export type Role = 'admin' | 'sales' | 'production' | 'warehouse' | 'quality' | 'finance' | 'viewer';

// 统一身份角色（方案 1）。Integral 登录后 user.role 可能是这些角色之一，
// 权限判定前会经 lib/permissions.ts 的映射展开为上面的岗位角色集合。
// 与后端 apps/core-api/src/identity/roles.js 的 ROLES 一致。
export type IdentityRole = 'company_admin' | 'engineer' | 'finance' | 'customer';

export interface User {
  id: string;
  username: string;
  display_name: string;
  role: Role | IdentityRole;
  language?: string;
  service?: boolean;
  /** 登录来源：user=旧 users 表账号，identity=统一身份，service=服务令牌。 */
  actorType?: 'user' | 'identity' | 'service';
  accountType?: 'employee' | 'customer';
  portalManage?: boolean;
  customerCode?: string | null;
}

export interface LoginResult {
  token: string;
  expires_at: string;
  user: User;
}

// ---- 客户 ----
export interface Customer {
  id: string;
  code: string;
  legal_name: string;
  trade_name?: string | null;
  tax_id?: string | null;
  tax_category?: string | null;
  currency: string;
  payment_terms_days: number;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country: string;
  active: number;
  created_at?: string;
  updated_at?: string;
}

// ---- 报价单 ----
export type QuotationStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
export interface Quotation {
  id: string;
  number: string;
  customer_id: string;
  quotation_date: string;
  valid_until?: string | null;
  currency: string;
  exchange_rate: number;
  status: QuotationStatus;
  subtotal: number;
  tax_total: number;
  grand_total: number;
  notes_zh?: string | null;
  terms_es?: string | null;
  // Phase-1 revision / audit columns (migration 009)
  version?: number;
  revision_of?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  // detail join
  customer_code?: string;
  customer_name?: string;
  customer_tax_id?: string | null;
  lines?: QuotationLine[];
}
export interface QuotationLine {
  id: string;
  quotation_id: string;
  item_id?: string | null;
  description_es: string;
  quantity: number;
  uom: string;
  unit_price: number;
  tax_rate: number;
  line_total: number;
  sort_order: number;
  // Phase-1 discount / list-price columns (migration 009)
  list_price?: number | null;
  discount_percentage?: number;
  discount_amount?: number;
}

// ---- 销售合同 ----
export type ContractStatus =
  | 'draft' | 'confirmed' | 'in_production' | 'ready'
  | 'partially_delivered' | 'completed' | 'cancelled';
export interface Contract {
  id: string;
  number: string;
  quotation_id?: string | null;
  customer_id: string;
  contract_date: string;
  delivery_due_date?: string | null;
  currency: string;
  exchange_rate: number;
  status: ContractStatus;
  subtotal: number;
  tax_total: number;
  grand_total: number;
  terms_es?: string | null;
  po_no?: string | null;
  legacy_erpnext_id?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  // list/detail join
  customer_code?: string;
  customer_name?: string;
  customer_tax_id?: string | null;
  customer_address?: string | null;
  customer_city?: string | null;
  customer_province?: string | null;
  customer_postal_code?: string | null;
  customer_tax_category?: string | null;
  quotation_number?: string | null; // 转合同来源报价单编号（快照 lineage）
  remaining_qty?: number; // 仅当 GET /api/contracts?deliverable=1 时返回：剩余可交数量合计
  /** 送修合同：质检不入库、交货走归还 */
  is_repair?: boolean;
  lines?: ContractLine[];
}
export interface ContractLine {
  id: string;
  contract_id: string;
  item_id?: string | null;
  description_es: string;
  quantity: number;
  delivered_quantity: number;
  uom: string;
  unit_price: number;
  tax_rate: number;
  line_total: number;
  sort_order: number;
  list_price?: number | null;
  discount_percentage?: number;
  discount_amount?: number;
  item_code?: string | null;
}

// ---- 交货单 ----
export type DeliveryStatus = 'draft' | 'confirmed' | 'dispatched' | 'delivered' | 'cancelled';
export interface Delivery {
  id: string;
  number: string;
  contract_id: string;
  customer_id: string;
  delivery_date: string;
  carrier?: string | null;
  tracking_number?: string | null;
  warehouse_id?: string | null;
  stock_movement_id?: string | null;
  status: DeliveryStatus;
  notes_es?: string | null;
  po_no?: string | null;
  issue_date?: string | null;
  point_of_sale?: string | null;
  remito_consecutive?: string | null;
  remito_r?: number;
  legacy_erpnext_id?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  // join
  customer_code?: string;
  customer_name?: string;
  customer_tax_id?: string | null;
  customer_address?: string | null;
  customer_city?: string | null;
  customer_province?: string | null;
  customer_postal_code?: string | null;
  customer_country?: string | null;
  customer_tax_category?: string | null;
  contract_number?: string;
  lines?: DeliveryLine[];
}
export interface DeliveryLine {
  id: string;
  delivery_id: string;
  contract_line_id?: string | null;
  item_id?: string | null;
  description_es: string;
  quantity: number;
  uom: string;
  serial_numbers?: string | null;
  batch_number?: string | null;
  sort_order: number;
  item_code?: string | null;
}

// ---- 公司财税抬头 /api/company（Remito R / RG 1415）----
export interface CompanyProfile {
  id: number;
  legal_name: string;
  trade_name?: string | null;
  cuit?: string | null;
  iibb?: string | null;
  vat_condition?: string | null;
  inicio_actividad?: string | null;
  commercial_address?: string | null;
  office_address?: string | null;
  city?: string | null;
  province?: string | null;
  country: string;
  postal_code?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  logo_url?: string | null;
  remito_document_code: string;
  default_point_of_sale?: string | null;
  cai?: string | null;
  cai_expires_at?: string | null;
  print_authorization_type?: string | null;
  remito_r_enabled: number;
  created_at?: string;
  updated_at?: string;
}

// ---- 正式 Remito 就绪检查 /api/deliveries/:id/remito-readiness ----
export interface RemitoMissingField { key: string; label: string; es: string; source: 'company' | 'delivery' | 'consignee'; }
export interface RemitoReadiness {
  ready: boolean;
  switchEnabled: boolean;
  formal: boolean;
  missing: RemitoMissingField[];
  point_of_sale: string | null;
  remito_document_code: string;
}

// ---- 全局聚合搜索 /api/search ----
export interface SearchHit {
  id: string;
  query: string;
  title: string;
  subtitle?: string;
  meta?: string;
  date?: string;
}
export interface SearchGroup {
  key: string;
  label: string;
  items: SearchHit[];
}
export interface SearchResult {
  query: string;
  total?: number;
  groups: SearchGroup[];
}

export interface ListParams {
  q?: string;
  year?: string;
  limit?: number;
  offset?: number;
  [k: string]: string | number | undefined;
}

// ---- 总览看板 /api/dashboard（全部为服务端真实聚合，无固定日期/汇率） ----
export interface DashboardMonth {
  month: string;
  contracts: number;
  total: number;
  net_total: number;
  tax_total: number;
  total_usd: number;
  net_usd: number;
}
export interface Dashboard {
  customers: number | null;
  new_leads: number | null;
  active_contracts: number | null;
  open_work_orders: number | null;
  pending_returns: number | null;
  receivable_ars: number | null;
  updated_at: string;
  scope: { year: string | null; month: string | null };
  revenue: {
    currency: string;
    contracts: number;
    total: number | null;
    net_total: number | null;
    tax_total: number | null;
    month_total: number | null;
    month_net_total: number | null;
    month: string;
    by_currency: Array<{ currency: string; contracts: number; total: number; net_total: number; total_usd: number }>;
  };
  monthly: DashboardMonth[];
  months: string[];
  years: string[];
}

// ---- 侧栏真实计数 /api/counts（key 与 navConfig.countKey 对齐）----
// 越权模块计数被后端置空（null），前端隐藏对应徽标。
export interface NavCounts {
  customers: number | null;
  quotations: number | null;
  contracts: number | null;
  deliveries: number | null;
  items: number | null;
  leads: number | null;
  work_orders: number | null;
  boms: number | null;
  operations: number | null;
  warehouses: number | null;
  suppliers: number | null;
  purchase_orders: number | null;
  price_lists: number | null;
  inspections: number | null;
  returns: number | null;
  arca_invoices: number | null;
  payments: number | null;
  users: number | null;
}

// ---- 经营洞察 /api/insights（真实漏斗 / 待办 / 预警）----
export interface InsightFunnelStage { key: string; label: string; count: number | null; }
export type InsightTone = 'ok' | 'info' | 'warn' | 'bad' | 'neutral';
export interface InsightTodo {
  key: string;
  label: string;
  count: number;
  route: string;
  tone: InsightTone;
}
export interface InsightAlert {
  key: string;
  tone: InsightTone;
  title: string;
  body: string;
  route: string;
}
export interface Insights {
  updated_at: string;
  funnel: InsightFunnelStage[];
  contracts_from_quotes: number;
  quote_to_contract_rate: number;
  todos: InsightTodo[];
  alerts: InsightAlert[];
}

// ---- 物料 ----
export type ItemType = 'product' | 'raw_material' | 'service' | 'consumable';
export type InventoryPolicy = 'powder' | 'consumable' | 'normal' | 'none';
export interface Item {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  description_es?: string | null;
  item_type: ItemType;
  stock_uom: string;
  sale_price: number;
  /** 采购成本仅 finance/admin 下发；其余角色响应中省略该字段 */
  purchase_cost?: number;
  currency: string;
  serial_tracked: number;
  batch_tracked: number;
  item_group?: string | null;
  active: number;
  inventory_policy?: InventoryPolicy;
  minimum_qty?: number;
  reorder_qty?: number;
  warning_enabled?: number;
  created_at?: string;
  updated_at?: string;
}

// ---- BOM / 工序 ----
export interface Bom {
  status?: 'draft' | 'approved' | 'deactivated';
  is_default?: number;
  approved_at?: string | null;
  id: string;
  number: string;
  item_id: string;
  quantity: number;
  version: number;
  active: number;
  notes?: string | null;
  item_code?: string;
  item_name_zh?: string;
  item_name_es?: string | null;
  lines?: BomLine[];
  operations?: BomOperation[];
}
export interface BomLine {
  id: string;
  bom_id: string;
  item_id: string;
  quantity: number;
  uom: string;
  operation_name?: string | null;
  item_code?: string;
  item_name_zh?: string;
  item_name_es?: string | null;
}
// 决策二：工序「是否需检」是质量的三态审核，不再是建 BOM 时顺手勾的布尔。
// qc_required 是由 qc_review_status 派生的只读投影，不可直接编辑。
// 注意与下面的 QcReviewStatus（质检【判据】的人工复核态）区分：这里判的是「这道工序要不要检」。
export type BomQcReviewStatus = 'pending' | 'required' | 'not_required';

export const BOM_QC_REVIEW_LABEL: Record<BomQcReviewStatus, string> = {
  pending: '待审核',
  required: '必须检验',
  not_required: '已确认免检',
};

export interface BomOperation {
  id: string;
  bom_id: string;
  operation_id: string;
  workstation?: string | null;
  time_minutes: number;
  hourly_cost: number;
  /** 派生字段：required → 1，pending / not_required → 0 */
  qc_required: number;
  qc_review_status?: BomQcReviewStatus | null;
  qc_checkpoint_id?: string | null;
  qc_review_reason?: string | null;
  qc_reviewed_by?: string | null;
  qc_reviewed_at?: string | null;
  description?: string | null;
  sort_order: number;
  operation_code?: string;
  operation_name?: string;
  operation_name_es?: string | null;
}

// GET /api/qc-review/queue —— 待审核 / 必检未绑检查点的 BOM 队列（质量工作台）
// 注意：粒度是「BOM」而非「工序行」：pending_count>0 或 required_without_checkpoint>0 才入队。
export interface QcReviewQueueBom {
  id: string;
  number: string;
  item_id: string;
  status: string;
  is_default: number;
  version: number;
  item_code: string;
  item_name_zh: string;
  item_active: number;
  operation_count: number;
  pending_count: number;
  required_count: number;
  not_required_count: number;
  required_without_checkpoint: number;
  complete: boolean;
}

export interface QcReviewQueueSummary {  boms_total: number;
  boms_default: number;
  boms_approved: number;
  items_covered: number;
  ops_total: number;
  ops_pending: number;
  ops_required: number;
  ops_not_required: number;
  ops_required_no_checkpoint: number;
}

export interface QcReviewQueue {
  boms: QcReviewQueueBom[];
  summary: QcReviewQueueSummary;
  scope_note: string;
}

// ---- 审计留痕 /api/audit/{quotations|contracts|deliveries}/:id ----
export interface AuditLogEntry {
  id: string;
  action: string;
  entity_number?: string | null;
  payload?: unknown;
  before?: unknown;
  after?: unknown;
  created_at: string;
  operator_name?: string | null;
  operator_username?: string | null;
}
export interface StatusHistoryEntry {
  id: string;
  from_status?: string | null;
  to_status: string;
  reason?: string | null;
  created_at: string;
  changed_by_name?: string | null;
}
export interface RecordAudit {
  audit: AuditLogEntry[];
  status: StatusHistoryEntry[];
}

// ---- 西语打印（沿用 ERPNext 模板的服务端渲染 HTML）----
// 'asset-return' = 送修工件退货单（公司→客户，退料/无法修复归还），格式沿用交货单，仅名称不同。
export type PrintKind = 'quotation' | 'contract' | 'delivery' | 'inspection' | 'asset-return';

// ---- 送修工件退料 / 退货单（/api/asset-return）----
export interface AssetReturnRecord {
  id: string;
  number: string;
  service_cycle_id: string;
  asset_id?: string | null;
  contract_id?: string | null;
  return_date?: string | null;
  reason: string;
  quantity: number;
  status: 'draft' | 'confirmed' | 'signed' | 'cancelled';
  notes?: string | null;
  signed_at?: string | null;
  signed_by?: string | null;
}

// ---- 工序 /api/operations ----
export interface Operation {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  workstation?: string | null;
  default_minutes: number;
  hourly_cost: number;
  qc_required: number;
  legacy_erpnext_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// ---- 仓库 /api/warehouses ----
export interface Warehouse {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  warehouse_type: string;
  active: number;
  legacy_erpnext_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// ---- 生产工单 /api/work-orders ----
export type WorkOrderStatus =
  | 'draft' | 'released' | 'in_progress' | 'completed' | 'cancelled' | 'voided';
export interface WorkOrder {
  id: string;
  number: string;
  business_type?: 'repair' | 'manufacturing';
  contract_id?: string | null;
  contract_line_id?: string | null;
  item_id: string;
  bom_id?: string | null;
  quantity: number;
  issued_for_quantity: number;
  produced_quantity: number;
  planned_start_date?: string | null;
  planned_end_date?: string | null;
  status: WorkOrderStatus;
  wip_warehouse_id?: string | null;
  finished_warehouse_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // 作废留痕：作废是终态，这三项是它可追责的唯一依据，不可为空
  voided_at?: string | null;
  voided_by?: string | null;
  void_reason?: string | null;
  // detail/list join（后端若未回传则前端按需补齐）
  item_code?: string;
  item_name_zh?: string;
  contract_number?: string | null;
  bom_number?: string | null;
  operations?: WorkOrderOperation[];
  // P6-5 收口 · 工艺方案来源（模板/方案）与建单时冻结的材料快照、完工成本快照。
  process_template_id?: string | null;
  material_plan_id?: string | null;
  process_template_number?: string | null;
  material_plan_code?: string | null;
  material_snapshots?: WorkOrderMaterialSnapshot[];
  cost?: WorkOrderCost | null;
  // 反向关联：绑定到该工单的客户来料（维修/加工，独立于公司库存）
  customer_supplied_materials?: CustomerSuppliedMaterial[];
  // 阶段6 批次可选规则：合同/工单显式置 batch_required=1 时才强制相关工序填写批次（默认可空、不阻断）。
  batch_required?: number;
  service_cycle_id?: string | null;
  // 绑定的维修版本摘要（后端 getWorkOrder join 回传；未绑定为 null）。工件身份/版本贯穿追溯的展示入口。
  service_cycle?: {
    id: string;
    version: number;
    status: AssetServiceCycleStatus;
    asset_id: string;
    customer_serial: string;
    internal_code?: string | null;
    customer_id?: string | null;
  } | null;
}

// —— 工件主档 + 维修版本（阶段6 · P6-2，激光熔覆返厂履历：同客户同号不报重复）——
export type PhysicalAssetStatus = 'active' | 'in_service' | 'delivered' | 'retired' | 'scrapped';
export type AssetServiceCycleStatus =
  | 'intake' | 'in_production' | 'inspected' | 'ready' | 'delivered' | 'returned' | 'closed';
export type AssetServiceCycleScenario = 'repair' | 'processing' | 'inspection_only' | 'other';

export interface AssetServiceCycle {
  id: string;
  asset_id: string;
  version: number;
  scenario: AssetServiceCycleScenario;
  status: AssetServiceCycleStatus;
  contract_id?: string | null;
  work_order_id?: string | null;
  intake_date?: string | null;
  returned_date?: string | null;
  findings?: string | null;
  recommendation?: string | null;
  legacy_qc_version?: string | null;
  notes?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  work_order_number?: string | null;
  contract_number?: string | null;
}

// 送修工件台账（GET /api/physical-assets/repair-ledger）
// 一行 = 一个送修工件的完整事实链：工件 / 维修履历 / 质检 / 工单 / 交货。
// 缺关联的行不会被过滤掉，而是以 pending_manual_link 显式标记 ——
// 台账漏记比多记更难发现，所以宁可显示「待关联」也不把行藏起来。
export interface RepairLedgerRow {
  asset_id: string;
  customer_serial: string;
  internal_code?: string | null;
  asset_type?: string | null;
  asset_status?: string | null;
  first_received_date?: string | null;
  asset_created_at?: string | null;
  item_code?: string | null;
  item_name_zh?: string | null;
  customer_id?: string | null;
  customer_code?: string | null;
  customer_name?: string | null;
  cycle_id?: string | null;
  cycle_version?: number | null;
  cycle_scenario?: string | null;
  cycle_status?: string | null;
  intake_date?: string | null;
  returned_date?: string | null;
  rework_count?: number | null;
  findings?: string | null;
  recommendation?: string | null;
  contract_id?: string | null;
  contract_number?: string | null;
  contract_status?: string | null;
  work_order_id?: string | null;
  work_order_number?: string | null;
  work_order_status?: string | null;
  produced_quantity?: number | null;
  work_order_legacy_id?: string | null;
  business_type?: string | null;
  inspection_count?: number | null;
  inspection_accepted?: number | null;
  /** 曾判不合格的张数。必须单列：返修后合格的件与一次过件不能长得一样 */
  inspection_rejected?: number | null;
  inspection_conditional?: number | null;
  /** 已建检验单但尚未出结论的张数，与 inspection_count=0（没检）语义不同 */
  inspection_pending?: number | null;
  last_inspection_date?: string | null;
  delivery_number?: string | null;
  delivery_status?: string | null;
  /** 可能有几个相关工单（仅提示，系统不自动关联） */
  candidate_work_orders?: number | null;
  pending_manual_link: boolean;
  pending_work_order: boolean;
}

export interface RepairLedgerSummary {
  assets: number;
  without_cycle: number;
  without_work_order: number;
  inspected: number;
  /** 至少有一张检验单判不合格的工件数 */
  with_rejected: number;
  contract_linked: number;
  pending_manual_link: number;
  has_candidates: number;
}

/** /api/physical-assets/repair-ledger 的响应。data 是行数组（ListPage 靠 unwrap 取），
 *  summary 与 scope_note 与 data 平级。 */
export interface RepairLedgerResponse {
  data: RepairLedgerRow[];
  summary: RepairLedgerSummary;
  scope_note: string;
}

export interface PhysicalAsset {
  id: string;
  customer_id: string;
  item_id?: string | null;
  customer_serial: string;
  internal_code?: string | null;
  asset_type?: string | null;
  description?: string | null;
  current_version: number;
  status: PhysicalAssetStatus;
  first_received_date?: string | null;
  notes?: string | null;
  legacy_erpnext_id?: string | null;
  legacy_qc_id?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  created_at?: string;
  updated_at?: string;
  cycle_count?: number;
  // detail join
  customer_code?: string;
  customer_name?: string;
  item_code?: string;
  item_name_zh?: string;
  item_name_es?: string;
  service_cycles?: AssetServiceCycle[];
}

// 同客户 + 同序列号命中候选（来料登记时由业务判定：新增维修版本 vs 另一件同号工件）。
export interface AssetMatchResult {
  customer_id: string;
  customer_serial: string;
  match_count: number;
  needs_resolution: boolean;
  resolution_options: string[];
  matches: PhysicalAsset[];
}

export interface AssetIntakeResult {
  resolution: 'new_asset' | 'new_cycle' | null;
  needs_resolution?: boolean;
  match_count?: number;
  prompt?: string;
  asset?: PhysicalAsset;
  cycle?: AssetServiceCycle;
  candidates?: Array<Pick<PhysicalAsset, 'id' | 'customer_serial' | 'item_id' | 'item_code' | 'asset_type' | 'current_version' | 'status'> & { latest_cycle?: AssetServiceCycle | null }>;
}

export interface AssetTraceHistoryRow {
  version: number;
  scenario: AssetServiceCycleScenario;
  status: AssetServiceCycleStatus;
  intake_date?: string | null;
  returned_date?: string | null;
  work_order_number?: string | null;
  stock_movements: string[];
  inspections: string[];
  deliveries: string[];
}

export interface AssetTrace {
  asset: PhysicalAsset;
  history: AssetTraceHistoryRow[];
  work_orders: Array<{ id: string; number: string; status: string; quantity: number; produced_quantity: number }>;
  stock_movements: Array<{ id: string; number: string; movement_type: string; movement_date: string; service_cycle_id?: string | null }>;
  quality_inspections: Array<{ id: string; number: string; inspection_date?: string | null; result?: string | null; process_type?: string | null; serial_number?: string | null; service_cycle_id?: string | null }>;
  deliveries: Array<{ id: string; number: string; status: string; delivery_date?: string | null; service_cycle_id?: string | null }>;
}
export interface WorkOrderOperation {
  id: string;
  work_order_id: string;
  operation_id: string;
  operation_code?: string;
  operation_name?: string;
  workstation?: string | null;
  planned_minutes: number;
  qc_required: number;
  // QC 回传的检验结论镜像（由质量系统维护）；工序执行 status 由生产维护，两者互不覆盖。
  qc_status?: 'not_required' | 'pending' | 'accepted' | 'rejected' | 'conditional';
  inspection_id?: string | null;
  sort_order: number;
  // P6-5 收口 · 建单时【冻结】的工费与币种（之后改模板/费率不影响本单）。
  hourly_cost?: number | null;
  cost_currency?: string | null;
  currency_suspect?: number;
  machine_cost?: number;
}

// P6-5 收口 · 工单冻结的材料快照行（复制自材料方案，改方案不影响历史单）。
export interface WorkOrderMaterialSnapshot {
  id: string;
  work_order_id: string;
  plan_id?: string | null;
  plan_code?: string | null;
  item_id: string;
  item_code?: string | null;
  item_name_zh?: string | null;
  quantity: number;
  uom: string;
  loss_rate: number;
  planned_consumption: number;
  batch_required: number;
  sort_order: number;
}

// P6-5 收口 · 完工成本快照（每工单一份，随完工按累计实际领料减退料 + 冻结工费重算）。
export interface WorkOrderCost {
  id: string;
  work_order_id: string;
  produced_quantity: number;
  machine_cost_ars: number;
  labor_currency_suspect: number;
  material_amount: number;
  material_currency?: string | null;
  material_ars: number;
  currency_mixed: number;
  breakdown?: Array<{ item_id: string; item_code?: string | null; currency: string; consumed_qty: number; unit_cost: number; amount: number; amount_ars: number | null }>;
  captured_at?: string;
  updated_at?: string;
}

// —— P6-5 收口 · 工艺方案三层：审批状态机（draft → approved → deactivated，停用不可回批准）——
export type PlanStatus = 'draft' | 'approved' | 'deactivated';

export interface ProcessTemplateOperation {
  id?: string;
  template_id?: string;
  operation_id: string;
  operation_code?: string | null;
  operation_name_zh?: string | null;
  operation_name_es?: string | null;
  sort_order: number;
  planned_minutes: number;
  hourly_cost?: number | null;          // 留空回落 operations.hourly_cost
  default_hourly_cost?: number | null;   // 工序主档默认费率（detail join）
  workstation?: string | null;
  qc_required: number;
  cost_currency?: string | null;         // 工费币种（默认 ARS）
  currency_suspect?: number;             // 历史误标 USD 等 → 保留原值 + 标记，不自动乘汇率
  default_cost_currency?: string | null;
  default_currency_suspect?: number;
  operation_qc_required?: number;
  default_minutes?: number | null;
  description?: string | null;
}

export interface ProcessTemplate {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  description?: string | null;
  active: number;
  status: PlanStatus;
  version: number;
  parent_id?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  legacy_erpnext_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // list/detail 计算列
  operation_count?: number;
  used?: boolean | number;
  operations?: ProcessTemplateOperation[];
}

export interface MaterialPlanLine {
  id?: string;
  plan_id?: string;
  item_id: string;
  item_code?: string | null;
  item_name_zh?: string | null;
  item_name_es?: string | null;
  quantity: number;
  uom: string;
  loss_rate: number;
  batch_required: number;
  sort_order: number;
  notes?: string | null;
}

export interface MaterialPlan {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  item_id?: string | null;
  notes?: string | null;
  active: number;
  status: PlanStatus;
  version: number;
  parent_id?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  legacy_erpnext_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // list/detail join
  item_code?: string | null;
  item_name_zh?: string | null;
  line_count?: number;
  used?: boolean | number;
  lines?: MaterialPlanLine[];
}

// —— 阶段6 · P6-6：BOM → 工艺方案（模板 + 材料方案）自动转换（POST /api/process-plans/convert-from-bom）——
export interface BomConversionOperation {
  operation_id: string;
  operation_code?: string | null;
  operation_name?: string | null;
  sort_order: number;
  planned_minutes: number;
  hourly_cost: number;
  workstation?: string | null;
  qc_required: number;
  cost_currency: string;
  currency_suspect: number;
  description?: string | null;
}
export interface BomConversionLine {
  item_id: string;
  item_code?: string | null;
  item_name_zh?: string | null;
  quantity: number;
  uom: string;
  loss_rate: number;
  batch_required: number;
  sort_order: number;
  notes?: string | null;
}
export interface BomConversionAnomaly {
  bom_number: string;
  bom_id: string;
  operation_id: string;
  operation_code?: string | null;
  operation_name?: string | null;
  original_value: number;
  original_currency: string;
  suggested_currency: string;
  confirmed: boolean;
}
export interface BomConversionResult {
  bom: {
    id: string; number: string; item_id: string;
    item_code?: string | null; item_name_zh?: string | null;
    version: number; legacy_erpnext_id?: string | null;
  };
  classification: string;
  template: {
    code: string; name_zh: string; status: PlanStatus | string;
    operation_count: number; machine_cost_preview: number; operations: BomConversionOperation[];
    id?: string; action?: 'created' | 'updated' | 'skipped' | string; reason?: string;
  };
  material_plan: {
    code: string; name_zh: string; item_id: string; status: PlanStatus | string;
    line_count: number; lines: BomConversionLine[];
    id?: string; action?: 'created' | 'updated' | 'skipped' | string; reason?: string;
  } | null;
  anomalies: BomConversionAnomaly[];
  created: boolean;
  bom_readonly?: boolean;
  historical_work_orders_untouched?: boolean;
  note?: string;
}

// ---- 库存流水 /api/stock/movements ----
export type StockMovementType =
  | 'receipt' | 'issue' | 'transfer' | 'adjustment' | 'delivery'
  | 'customer_return' | 'supplier_return' | 'production_consume' | 'production_return' | 'production_output';
export interface StockMovement {
  id: string;
  number: string;
  movement_type: StockMovementType;
  movement_date: string;
  reference_type?: string | null;
  reference_id?: string | null;
  status: string;
  notes?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  lines?: StockMovementLine[];
}
export interface StockMovementLine {
  id: string;
  movement_id: string;
  item_id: string;
  from_warehouse_id?: string | null;
  to_warehouse_id?: string | null;
  quantity: number;
  uom: string;
  serial_numbers?: string | null;
  batch_number?: string | null;
  sort_order: number;
}

// 库存流水查询（GET /api/stock/movements）：行带明细与联表显示字段
export interface StockLedgerLine extends StockMovementLine {
  item_code?: string | null;
  item_name_zh?: string | null;
  from_warehouse_code?: string | null;
  from_warehouse_name?: string | null;
  to_warehouse_code?: string | null;
  to_warehouse_name?: string | null;
  asset_id?: string | null;
  service_cycle_id?: string | null;
  asset_serial?: string | null;
}

export interface StockLedgerMovement extends StockMovement {
  work_order_number?: string | null;
  work_order_business_type?: string | null;
  lines?: StockLedgerLine[];
}

export interface StockLedgerResponse {
  data: StockLedgerMovement[];
  total: number;
  limit: number;
  offset: number;
}
export interface StockBalance {
  item_id: string;
  item_code: string;
  name_zh: string;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  quantity: number;
  stock_uom?: string;
  inventory_policy?: InventoryPolicy;
}

export type StockWarningLevel = 'red' | 'orange' | 'ok';
export interface StockWarningItem {
  item_id: string;
  item_code: string;
  name_zh: string;
  name_es?: string | null;
  inventory_policy: InventoryPolicy;
  stock_uom: string;
  quantity: number;
  minimum_qty: number;
  reorder_qty: number;
  warning_enabled: boolean;
  open_plan_qty: number;
  projected_qty: number;
  in_transit_qty: number;
  level: StockWarningLevel;
  warehouses?: StockBalance[];
}

export interface StockCountLine {
  id?: string;
  item_id: string;
  warehouse_id: string;
  book_qty: number;
  actual_qty: number;
  variance: number;
  reason?: string | null;
  uom?: string | null;
  item_code?: string;
  item_name_zh?: string;
  warehouse_code?: string;
}

export interface StockCount {
  id: string;
  number: string;
  count_date: string;
  warehouse_id?: string | null;
  status: 'draft' | 'confirmed' | 'cancelled';
  notes?: string | null;
  lines?: StockCountLine[];
  created_at?: string;
}

// ---- 客户来料（维修/加工）/api/customer-supplied-materials ----
// 独立业务对象：客户送来的零件与公司自有库存分开记录，不写库存流水、不改采购成本。
// 可关联 客户（必填）/ 序列号 / 工单 / 交还单；工单与物料均可空（客户件可能不是公司物料主档）。
export type CustomerSuppliedMaterialStatus =
  | 'received' | 'in_process' | 'awaiting_return' | 'returned' | 'scrapped';
export type CustomerSuppliedScenario = 'repair' | 'processing' | 'other';
export interface CustomerSuppliedMaterial {
  id: string;
  number: string;
  customer_id: string;
  work_order_id?: string | null;
  contract_id?: string | null;
  item_id?: string | null;
  scenario: CustomerSuppliedScenario;
  description?: string | null;
  serial_number?: string | null;
  quantity: number;
  uom: string;
  incoming_condition?: string | null;
  received_date: string;
  source_reference?: string | null;
  storage_location?: string | null;
  status: CustomerSuppliedMaterialStatus;
  return_id?: string | null;
  returned_date?: string | null;
  notes?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // detail/list join
  customer_code?: string;
  customer_name?: string;
  item_code?: string | null;
  item_name_zh?: string | null;
  item_name_es?: string | null;
  work_order_number?: string | null;
  return_number?: string | null;
}

// ---- 客户来料交还单 /api/customer-supplied-materials/returns ----
export type CustomerMaterialReturnHandling =
  | 'repaired' | 'processed' | 'scrapped' | 'partially_returned';
export interface CustomerMaterialReturn {
  id: string;
  number: string;
  customer_id: string;
  work_order_id?: string | null;
  return_date: string;
  handling: CustomerMaterialReturnHandling;
  repair_outcome?: 'repaired' | 'unrepairable' | 'partial';
  status: 'draft' | 'completed' | 'cancelled';
  notes?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // list/detail join
  customer_code?: string;
  customer_name?: string;
  work_order_number?: string | null;
  line_count?: number;
  lines?: CustomerMaterialReturnLine[];
}
export interface CustomerMaterialReturnLine {
  id: string;
  return_id: string;
  supplied_material_id: string;
  quantity: number;
  serial_number?: string | null;
  outgoing_condition?: string | null;
  notes?: string | null;
  sort_order: number;
  // join
  material_number?: string;
  material_serial?: string | null;
  item_code?: string | null;
  item_name_zh?: string | null;
}

// ---- 供应商 /api/suppliers（采购来源主数据）----
export interface Supplier {
  id: string;
  code: string;
  legal_name: string;
  tax_id?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  active: number;
  legacy_erpnext_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// ---- 采购订单 /api/purchase-orders（国内 ARS / 进口外币两类）----
// 收货入库经 POST /:id/receipts 过账标准 receipt 库存流水；本模块【不改 items.purchase_cost】，
// 到岸成本/运费分摊属后续 P3-2/P3-3（费用先分类并保留原始币种与折算依据）。
export type PurchaseStatus =
  | 'draft' | 'confirmed' | 'partially_received' | 'received' | 'cancelled';
export type PurchaseType = 'domestic' | 'import';
export interface PurchaseOrder {
  id: string;
  number: string;
  supplier_id: string;
  order_date: string;
  expected_date?: string | null;
  purchase_type: PurchaseType;
  currency: string;
  exchange_rate: number;
  status: PurchaseStatus;
  subtotal: number;
  tax_total: number;
  grand_total: number;
  estimated_total_ars: number;
  notes?: string | null;
  legacy_erpnext_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // join（list/detail）
  supplier_name?: string;
  lines?: PurchaseOrderLine[];
  charges?: PurchaseOrderCharge[];
  receipts?: PurchaseReceipt[];
}
export interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  item_id: string;
  description?: string | null;
  quantity: number;
  received_quantity: number;
  uom: string;
  unit_price: number;
  tax_rate: number;
  line_total: number;
  sort_order: number;
  // join（detail）
  item_code?: string;
  item_name?: string;
}
export interface PurchaseOrderCharge {
  id: string;
  purchase_order_id: string;
  charge_type: string;
  description?: string | null;
  calculation_base: number;
  rate: number;
  amount: number;
  currency: string;
  exchange_rate: number;
  amount_ars: number;
  recoverable: number;
  sort_order: number;
}

// ---- 采购收货单 /api/purchase-orders/:id/receipts（Goods Receipt，收货即入库）----
export interface PurchaseReceipt {
  id: string;
  number: string;
  purchase_order_id: string;
  supplier_id: string;
  receipt_date: string;
  supplier_document?: string | null;
  stock_movement_id?: string | null;
  status: 'posted' | 'cancelled';
  notes?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // join
  supplier_name?: string;
  purchase_order_number?: string;
  lines?: PurchaseReceiptLine[];
}
export interface PurchaseReceiptLine {
  id: string;
  receipt_id: string;
  purchase_order_line_id: string;
  item_id: string;
  quantity: number;
  uom: string;
  to_warehouse_id: string;
  unit_price: number;
  notes?: string | null;
  sort_order: number;
  // join
  item_code?: string;
  item_name?: string;
  warehouse_name?: string;
}

// ---- 退货 / 退料 /api/returns（客户退货 RMA + 供应商退料 RTS，独立于自有库存的正常出入库方向）----
// customer_return：客户把货退回公司 → resolve 时按处理方式「回库」流入仓库（库存 +）。
// supplier_material_return：公司把原材料退回供应商 → resolve 时必须「退还供应商」，从仓库流出（库存 −）。
// 状态：draft→(authorized/received/inspected)→resolved；resolved/cancelled 后不可重复处理（后端 409）。
export type ReturnType = 'customer_return' | 'supplier_material_return';
export type ReturnStatus =
  | 'draft' | 'authorized' | 'received' | 'inspected' | 'resolved' | 'cancelled';
export type ReturnDisposition =
  | 'restock' | 'repair' | 'replace' | 'scrap' | 'return_to_supplier';
export type InspectionResult = 'pending' | 'accepted' | 'rejected' | 'conditional';

// P1-7：不合格处置枚举，口径与 core-api 的 DISPOSITIONS 严格一致。
// 现场（QC）写入，控制台展示与登记；两侧共用同一份定义，避免枚举漂移。
export type InspectionDisposition =
  | 'rework'            // 返工（同版本内，不升版本）
  | 're_clad'           // 重新熔覆
  | 're_machine'        // 重新加工
  | 'scrap'             // 报废（维修受托保管件禁止）
  | 'concession'        // 让步接收
  | 'return_to_supplier'// 退回供应商
  | 'return_original';  // 无法修复·原件退回（走交还单）
export interface Return {
  id: string;
  number: string;
  return_type: ReturnType;
  customer_id?: string | null;
  supplier_id?: string | null;
  contract_id?: string | null;
  delivery_id?: string | null;
  return_date: string;
  reason_code: string;
  reason_detail?: string | null;
  status: ReturnStatus;
  disposition?: ReturnDisposition | null;
  warehouse?: string | null;
  warehouse_id?: string | null;
  stock_movement_id?: string | null;
  credit_note_reference?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  // join（list/detail）
  customer_code?: string | null;
  customer_name?: string | null;
  supplier_code?: string | null;
  supplier_name?: string | null;
  contract_number?: string | null;
  delivery_number?: string | null;
  line_count?: number;
  lines?: ReturnLine[];
}
export interface ReturnLine {
  id: string;
  return_id: string;
  item_id: string;
  quantity: number;
  uom: string;
  serial_numbers?: string | null; // JSON 字符串数组
  batch_number?: string | null;
  inspection_result: InspectionResult;
  notes?: string | null;
  sort_order: number;
  // join（detail）
  item_code?: string | null;
  item_name_zh?: string | null;
}

// —— QC 接管（第六步）· 质检模板 / 量化公差人工复核页 ——
// 双层验收：criterion_text（qi-inspect 描述）恒保留；量化字段（comparator/target_value/min_value/max_value/tolerance）
// 来自 ERPNext 导入或人工指派，确认前 review_status='pending_review'、auto_usable=0，绝不据此自动判定合格。
export type QcReviewStatus = 'pending_review' | 'confirmed' | 'rejected';
export type QcCriterionSource = 'qi-inspect-text' | 'erpnext-imported' | 'manual';
export type QcComparator = '>=' | '<=' | 'tolerance';
export type QcInspectionKind = 'incoming' | 'in_process' | 'final';

export interface QcTemplateRow {
  id: string;
  code: string;
  name_zh: string;
  process_type?: string | null;
  description?: string | null;
  active: number;
  source_system?: string | null;
  // list 计数列
  stage_count?: number;
  checkpoint_count?: number;
  pending_review_count?: number;
  quantified_count?: number;
}

export interface QcCriterion {
  id: string;
  checkpoint_id: string;
  parameter_id?: string | null;
  template_id: string;
  criterion_text?: string | null;
  comparator?: QcComparator | null;
  target_value?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  tolerance?: string | null;
  unit?: string | null;
  numeric: number;
  review_status: QcReviewStatus;
  auto_usable: number;
  source: QcCriterionSource;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
  // join
  parameter_key?: string | null;
  parameter_label?: string | null;
  parameter_unit?: string | null;
  value_type?: string | null;
}

export interface QcCheckpointParameter {
  checkpoint_id: string;
  points: number;
  required: number;
  parameter_key: string;
  parameter_label?: string | null;
  parameter_unit?: string | null;
}

export interface QcCheckpoint {
  id: string;
  template_id: string;
  stage_id: string;
  checkpoint_key: string;
  name_zh: string;
  inspection_kind?: QcInspectionKind | null;
  description?: string | null;
  sort_order: number;
  stage_key?: string | null;
  stage_name?: string | null;
  stage_order?: number | null;
  parameters: QcCheckpointParameter[];
  criteria: QcCriterion[];
}

export interface QcStage {
  id: string;
  stage_key: string;
  stage_name: string;
  stage_order: number;
  checkpoints: QcCheckpoint[];
}

export interface QcTemplateDetail extends QcTemplateRow {
  stages: QcStage[];
}

export type QcCandidateReason = 'template_unmapped' | 'parameter_unclassified' | 'no_value' | 'no_checkpoint' | 'checkpoint_ambiguous';
export type QcCandidateStatus = 'pending_review' | 'assigned' | 'dismissed';

export interface QcImportCandidate {
  id: string;
  source_template_name: string;
  target_template_code?: string | null;
  parameter_key?: string | null;
  stage_hint?: string | null;
  specification: string;
  raw_value?: string | null;
  comparator?: QcComparator | null;
  target_value?: number | null;
  max_value?: number | null;
  tolerance?: string | null;
  unit?: string | null;
  numeric: number;
  reason: QcCandidateReason;
  review_status: QcCandidateStatus;
  source_system: string;
  source_ref?: string | null;
  final_checkpoint_id?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface QcCandidateSummary {
  total: number;
  by_reason: Partial<Record<QcCandidateReason, number>>;
  by_review_status: Partial<Record<QcCandidateStatus, number>>;
  quantified_bound: number;
}

// ---- 财务：ARCA 税票与收款（I02/I03 只读基线；字段严格对齐 core-api src/finance-read.js）----
export type ArcaVoucherType = 'Factura A' | 'Factura B' | 'Nota de Credito A' | 'Nota de Debito A';
export type ArcaInvoiceStatus = 'draft' | 'authorized' | 'rejected' | 'cancelled';

export interface ArcaInvoice {
  id: string;
  internal_number: string;
  contract_id?: string | null;
  customer_id: string;
  voucher_type: ArcaVoucherType | string;
  point_of_sale: string;
  voucher_number?: string | number | null;
  invoice_date: string;
  due_date?: string | null;
  currency: string;
  exchange_rate: number;
  net_total: number;
  tax_total: number;
  grand_total: number;
  grand_total_ars: number;
  cae?: string | null;
  cae_due_date?: string | null;
  qr_url?: string | null;
  status: ArcaInvoiceStatus | string;
  source_system?: string | null;
  source_id?: string | null;
  created_at?: string;
  // join / 派生
  customer_code?: string | null;
  customer_name?: string | null;
  customer_tax_id?: string | null;
  contract_number?: string | null;
  paid_amount?: number;
  /** 未收余额 = ARS 总额 − 已posted收款核销额。不代表「未到账」，到账口径待财务确认。 */
  outstanding_amount?: number;
}


export interface ArcaInvoicePayload {
  id: number;
  invoice_id?: string | null;
  source_system: string;
  source_id: string;
  kind: 'draft' | 'result';
  payload_json: string;
  sha256: string;
  received_at: string;
}

export type PaymentMethod = string;
export type PaymentStatus = 'draft' | 'posted' | 'cancelled';

export interface PaymentAllocation {
  id: string;
  invoice_id: string;
  amount: number;
  internal_number?: string | null;
  voucher_type?: string | null;
  point_of_sale?: string | null;
  voucher_number?: string | number | null;
  invoice_date?: string | null;
  currency?: string | null;
  grand_total_ars?: number | null;
  invoice_status?: string | null;
  cae?: string | null;
  invoice_paid_amount?: number;
  /** 该发票「现在」的未收余额（会随后续收款变小），与 allocation.amount 不是同一个数。 */
  invoice_outstanding_amount?: number;
}

export interface Payment {
  id: string;
  number: string;
  customer_id: string;
  payment_date: string;
  currency: string;
  amount: number;
  exchange_rate: number;
  method: PaymentMethod;
  reference?: string | null;
  notes?: string | null;
  status: PaymentStatus | string;
  source_system?: string | null;
  source_id?: string | null;
  created_at?: string;
  // join / 派生
  customer_code?: string | null;
  customer_name?: string | null;
  allocated_amount?: number;
  unallocated_amount?: number;
  /** 仅展示用推算（原币 × 汇率）；原币与 ARS 分别落库属未完成项。 */
  amount_ars_equivalent?: number;
  allocations?: PaymentAllocation[];
}

// ---- 附件基础模型（元数据；文件字节在服务端独立目录）----
export interface Attachment {
  id: string;
  entity_type: 'arca_invoice' | 'payment' | 'contract' | 'delivery' | 'inspection' | 'return' | string;
  entity_id: string;
  kind: string;
  original_name: string;
  stored_name?: string;
  mime_type?: string | null;
  byte_size: number;
  sha256: string;
  source_system?: string | null;
  uploaded_by?: string | null;
  uploaded_by_label?: string | null;
  created_at: string;
}

/** 列表端点附带的分页元数据（门控开启时后端才返回；旧端点无此字段）。 */
export interface ListMeta {
  limit?: number;
  offset?: number;
  total?: number;
  has_more?: boolean;
}